// Create requests always use the lazily loaded v2 adapters. The adapters retain the screen's response shapes;
// v2 remains responsible for ownership, prices, safety and refunds. Configuration/chunk failures return503.
import { requestV2 } from '../../../lib/api/v2-only.ts'
import { myFolders } from '../../../lib/api/folders.ts'
import { isV2Media } from '../../../lib/api/media-ids.ts'

const CLOUD = process.env.NEXT_PUBLIC_PROMPTCHAN_CLOUD
const FREE = process.env.NEXT_PUBLIC_PROMPTCHAN_FREE

type Body = Record<string, unknown>

/**
 * The backend an image request goes to: Free accounts and any request with an AI Character use the free
 * pool's host, paid accounts the main one. (Both are one backend since September 2026; the hosts still
 * differ.) A remembered job is collected from the same host (src/lib/image-jobs.js).
 */
export const imageBackend = ({
  paid,
  withCharacter,
}: {
  paid: boolean
  withCharacter: boolean
}) => (!paid || withCharacter ? FREE : CLOUD)

/** Generate through v2; legacy arguments remain for pending-job bookkeeping until its bounded drain ends. */
export const generateImage = (
  apiBase: string | undefined,
  model: string,
  token: string,
  body: Body,
) =>
  requestV2(
    () => import('../../../lib/api/create-v2.ts'),
    (loaded) => loaded.picture(token, body),
  )

/** The person's gems now, read again from v2 (create-v2.ts `balance`): `{ gems }`. */
export const gemsNow = (token: string) =>
  requestV2(
    () => import('../../../lib/api/create-v2.ts'),
    (loaded) => loaded.balance(token),
  )

export const tweakImage = (token: string, body: Body) =>
  requestV2(
    () => import('../../../lib/api/create-v2.ts'),
    (loaded) => loaded.edit(token, body),
  )

type VideoSides = typeof import('../../../lib/api/video-v2.ts')

export const submitVideo = (token: string, body: Body) =>
  requestV2(
    () => import('../../../lib/api/video-v2.ts'),
    (loaded) => loaded.submit(token, body),
  )

/** Poll a current order or a remembered earlier job through its owner-scoped v2 read. */
const poll =
  (
    use: (
      loaded: VideoSides,
    ) => (token: string, id: string) => Promise<Response>,
  ) =>
  (token: string, requestId: string) =>
    requestV2(
      () => import('../../../lib/api/video-v2.ts'),
      (loaded) => use(loaded)(token, requestId),
    )

export const videoStatus = poll((loaded) => loaded.status)
export const videoStatusWithLogs = poll((loaded) => loaded.statusWithLogs)
export const videoResult = poll((loaded) => loaded.result)

/**
 * The person's recent video jobs: on v2, v2's orders first, then earlier jobs read directly by v2.
 * `withV1` false (before the panel opens) asks only v2's orders.
 */
export const listVideoJobs = (token: string, withV1 = true) =>
  requestV2(
    () => import('../../../lib/api/video-v2.ts'),
    (loaded) => loaded.history(token, withV1),
  )

/** Claim an eligible earlier failed job through v2's atomic refund. */
export const claimFailedVideo = (token: string, requestId: string) =>
  requestV2(
    () => import('../../../lib/api/video-v2.ts'),
    (loaded) => loaded.claimEarlier(token, requestId),
  )

// Keeping results

export type PublishBody = {
  /** An image: its URL or base64. A video: its document id. */
  image: string
  folder: string | null
  visibility: 'Public' | 'Private'
}

/** New v2 results stay on v2. Earlier results keep their existing publication path until the paid-result drain. */
export const publish = (
  token: string,
  kind: 'image' | 'video',
  body: PublishBody,
  mediaId?: string | null,
) => {
  const id = mediaId || (kind === 'video' ? body.image : null)
  if (isV2Media(id))
    return requestV2(
      () => import('../../../lib/api/create-v2.ts'),
      (loaded) => loaded.keep(token, {}, id, body.visibility, body.folder),
    )
  // Bounded compatibility for results made before direct-v2 generation. No new request is generated here.
  if (!CLOUD)
    return Promise.resolve(
      new Response(
        JSON.stringify({
          error: 'This request is temporarily unavailable. Please try again.',
          code: 'unavailable',
          retryable: true,
        }),
        { status: 503, headers: { 'Content-Type': 'application/json' } },
      ),
    )
  return fetch(
    `${CLOUD}/api/${kind === 'video' ? 'publish_video' : 'publish_image'}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: token },
      body: JSON.stringify(body),
    },
  )
}

/**
 * A picture order on v2 remembered from before a reload (src/lib/image-jobs.js `noteOrder`), followed to its end and
 * answered in the page's recovery shape, or null.
 */
export const recoverOrder = async (token: string, orderId: string) => {
  try {
    const loaded = await import('../../../lib/api/create-v2.ts')
    return await loaded.recoverPicture(token, orderId)
  } catch {
    return null
  }
}

/** Delete an owned item through v2, including items made before the rebuild. */
export const deleteMedia = (
  token: string,
  body: { imageId: string; imageUid: string },
) =>
  requestV2(
    () => import('../../../lib/api/create-v2.ts'),
    (loaded) => loaded.remove(token, {}, body.imageId),
  )

// Selection actions apply v2's per-item ownership and safety checks to every item.

const successResponse = (body: Body) =>
  new Response(JSON.stringify(body), {
    status: 200,
    headers: { 'Content-Type': 'application/json', 'X-Answered-By': 'v2' },
  })

/** Publish the selected items with v2's safety checks, preserving bounded frame-preparation retries. */
export const moveToPublic = (token: string, ids: string[]): Promise<Response> =>
  requestV2(
    () => import('../../../lib/api/create-v2.ts'),
    (loaded) => loaded.makePublic(token, {}, ids),
  )

/** Delete selected items through their owner-scoped v2 action; already-gone items are skipped. */
export async function deleteMany(
  token: string,
  ids: string[],
): Promise<Response> {
  for (const id of ids) {
    const response = await deleteMedia(token, { imageId: id, imageUid: '' })
    if (!response.ok && response.status !== 404) return response
  }
  return successResponse({
    status: 'success',
    message: 'Images deleted successfully',
  })
}

// Saved workflows use v2 directly, with the response shapes the existing bookmark screen reads.

/** Save a result's prompt and settings as a workflow. */
export const savePrompt = (token: string, imageId: string) =>
  requestV2(
    () => import('../../../lib/api/create-v2.ts'),
    (loaded) => loaded.savePrompt(token, {}, imageId),
  )

/** The person's saved picture or clip workflows. */
export const listSavedPrompts = (token: string, video?: boolean) =>
  requestV2(
    () => import('../../../lib/api/create-v2.ts'),
    (loaded) => loaded.savedPrompts(token, {}, video),
  )

/** Rename a saved workflow. */
export const renameSavedPrompt = (
  token: string,
  promptId: string,
  name: string,
) =>
  requestV2(
    () => import('../../../lib/api/create-v2.ts'),
    (loaded) => loaded.renamePrompt(token, {}, promptId, name),
  )

/** Delete a saved workflow. */
export const deleteSavedPrompt = (token: string, promptId: string) =>
  requestV2(
    () => import('../../../lib/api/create-v2.ts'),
    (loaded) => loaded.deletePrompt(token, {}, promptId),
  )

/** The person's public folders. */
export const publicFolders = (token: string) => myFolders(token, 'public')

/** The person's private folders (src/lib/api/folders.ts). */
export const privateFolders = (token: string) => myFolders(token, 'private')

// Measurement: the caller never waits on these or shows their failure.

const VALUES = ['up', 'down'] as const
const ACTIONS = [
  'download',
  'create_character',
  'use_character',
  'publish',
  'publish_video',
  'share',
  'save',
] as const

/** Feedback uses the result ID; generated v2 results retain one even when unsaved. */
export const sendOutputFeedback = (token: string, body: Body) =>
  requestV2(
    () => import('../../../lib/api/create-v2.ts'),
    (loaded) => {
      const mediaId = typeof body.media_id === 'string' ? body.media_id : null
      const value = VALUES.find((v) => v === body.value)
      return mediaId && value
        ? loaded.rate(token, {}, mediaId, value)
        : Promise.resolve(invalidMeasurement())
    },
  )

export const reportEarlyAccessBlocked = (token: string, body: Body) =>
  requestV2(
    () => import('../../../lib/api/create-v2.ts'),
    (loaded) => {
      const mediaId = typeof body.media_id === 'string' ? body.media_id : null
      const action = ACTIONS.find((a) => a === body.action)
      return mediaId && action
        ? loaded.earlyAccessAttempt(token, {}, mediaId, action)
        : Promise.resolve(invalidMeasurement())
    },
  )

const invalidMeasurement = () =>
  new Response(
    JSON.stringify({
      error: 'A media ID is required.',
      code: 'invalid_request',
      retryable: false,
    }),
    { status: 400, headers: { 'Content-Type': 'application/json' } },
  )
